import { DispatchCard } from 'src/entities/dispatch';

export type DropPreview = { rowId: string; index: number };

export type BoardInteractionState = {
  activeCard: DispatchCard | null;
  dragCount: number;
  dropPreview: DropPreview | null;
  recentlyMovedCardIds: ReadonlySet<string>;
  selectedCardIds: ReadonlySet<string>;
};

export type BoardInteractionStore = {
  getState: () => BoardInteractionState;
  subscribe: (listener: () => void) => () => void;
  setSelectedCardIds: (next: ReadonlySet<string>) => void;
  clearSelectedCards: () => void;
  startDrag: (card: DispatchCard | null, count: number) => void;
  setDropPreview: (preview: DropPreview | null) => void;
  endDrag: () => void;
  flashRecentlyMoved: (ids: string[]) => void;
  reset: () => void;
  dispose: () => void;
};

export const RECENTLY_MOVED_DURATION_MS = 2800;

const EMPTY_IDS: ReadonlySet<string> = new Set<string>();

const INITIAL_STATE: BoardInteractionState = {
  activeCard: null,
  dragCount: 0,
  dropPreview: null,
  recentlyMovedCardIds: EMPTY_IDS,
  selectedCardIds: EMPTY_IDS,
};

export function createBoardInteractionStore(): BoardInteractionStore {
  let state = INITIAL_STATE;
  let listeners = new Set<() => void>();
  let flashTimer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;

  // commit が弾くのは「同一参照が渡されたとき」と「dispose 後」だけで、値の
  // 中身が同じでも新しいオブジェクトを渡せば listener は発火する（重複排除はしない）。
  // 事前判定しているのは clearSelectedCards/setDropPreview/endDrag だけで、
  // setSelectedCardIds/startDrag は無条件に commit を呼ぶ（同値でも通知が飛ぶ）。
  //
  // 後続の hook 実装者への注意: 同値で呼んでも listener は発火するので、
  // useSyncExternalStore の getSnapshot はプリミティブか state 内の既存参照を
  // 返し続けること（毎回新しいオブジェクトを返すと無限ループになる）。
  const commit = (next: BoardInteractionState) => {
    if (disposed || next === state) return;
    state = next;
    listeners.forEach((l) => { l(); });
  };

  const clearFlashTimer = () => {
    if (flashTimer == null) return;
    clearTimeout(flashTimer);
    flashTimer = null;
  };

  return {
    getState: () => state,
    subscribe: (listener) => {
      if (disposed) return () => {};
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    // Why not 同値ガード: Set の同値比較は size + 要素走査が要る。呼び出し元
    // （選択操作・ドラッグ開始）は実際に値が変わる場面でしか呼ばないので、
    // その複雑さに見合わない。
    setSelectedCardIds: (next) => { commit({ ...state, selectedCardIds: next }); },
    clearSelectedCards: () => {
      if (state.selectedCardIds.size === 0) return;
      commit({ ...state, selectedCardIds: EMPTY_IDS });
    },
    startDrag: (card, count) => { commit({ ...state, activeCard: card, dragCount: count, dropPreview: null }); },
    setDropPreview: (preview) => {
      // 同じ行の同じ位置なら通知しない。dragOver はホバーごとに飛んでくる
      const cur = state.dropPreview;
      if (cur === preview) return;
      if (cur && preview && cur.rowId === preview.rowId && cur.index === preview.index) return;
      commit({ ...state, dropPreview: preview });
    },
    endDrag: () => {
      // dragEnd/dragCancel の両方から呼ばれる経路があるので、既に idle なら
      // 素通りさせて無駄な通知を避ける
      if (state.activeCard === null && state.dragCount === 0 && state.dropPreview === null) return;
      commit({ ...state, activeCard: null, dragCount: 0, dropPreview: null });
    },
    flashRecentlyMoved: (ids) => {
      // dispose 後も setTimeout 自体は無条件にスケジュールされてしまうので、
      // ここで弾く。タイマを張るアクションはこれだけなので、他のアクションは
      // commit 側の disposed チェックに任せている。
      if (disposed || ids.length === 0) return;
      clearFlashTimer();
      commit({ ...state, recentlyMovedCardIds: new Set(ids) });
      flashTimer = setTimeout(() => {
        flashTimer = null;
        commit({ ...state, recentlyMovedCardIds: EMPTY_IDS });
      }, RECENTLY_MOVED_DURATION_MS);
    },
    reset: () => {
      clearFlashTimer();
      commit(INITIAL_STATE);
    },
    dispose: () => {
      clearFlashTimer();
      disposed = true;
      listeners = new Set();
    },
  };
}
